/// <reference types="google.maps" />
import { Injectable } from '@angular/core';

import { DirectionsService } from '../../directions/directions.service';
import { IRoutingService, RouteOptions, RoutePoint, RouteResult } from '../routing.types';

@Injectable({ providedIn: 'root' })
export class DirectionsRoutingService implements IRoutingService {
  private cache: Map<string, RouteResult> = new Map();
  private readonly CACHE_PREFIX = 'directions_routing_cache_';
  private readonly CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  constructor(private readonly directions: DirectionsService) {
    this.loadCacheFromStorage();
  }

  async computeRoute(origin: RoutePoint, destination: RoutePoint, options: RouteOptions = {}): Promise<RouteResult> {
    const cacheKey = this.generateCacheKey(origin, destination, options);

    // Check memory cache first
    const cachedResult = this.cache.get(cacheKey);
    if (cachedResult) {
      return cachedResult;
    }

    // Check localStorage cache
    const storedCache = this.getFromStorage(cacheKey);
    if (storedCache) {
      this.cache.set(cacheKey, storedCache);
      return storedCache;
    }

    const waypoints: google.maps.DirectionsWaypoint[] = (options.waypoints || []).map((w) => ({
      location: new google.maps.LatLng(w.lat, w.lng),
      stopover: true,
    }));

    const res = await this.directions.getDirections({
      origin: new google.maps.LatLng(origin.lat, origin.lng),
      destination: new google.maps.LatLng(destination.lat, destination.lng),
      waypoints,
      optimizeWaypoints: true,
      travelMode: (options.travelMode === 'DRIVE'
        ? 'DRIVING'
        : options.travelMode || 'DRIVING') as google.maps.TravelMode,
    });

    const overviewPath = res.routes[0].overview_path;
    // Check if geometry library is available
    if (!google.maps.geometry || !google.maps.geometry.encoding) {
      throw new Error(
        'Google Maps Geometry library is not loaded. Please ensure the geometry library is included in your Google Maps script.',
      );
    }

    const encodedPolyline = google.maps.geometry.encoding.encodePath(overviewPath as google.maps.LatLng[]);

    // Distance and duration from legs
    let distanceMeters = 0;
    let durationSeconds = 0;
    const legs = res.routes[0].legs.map((l) => {
      distanceMeters += l.distance?.value || 0;
      durationSeconds += l.duration?.value || 0;
      return { distanceMeters: l.distance?.value || 0, duration: l.duration?.value || 0 };
    });

    const result: RouteResult = {
      encodedPolyline,
      distanceMeters,
      durationSeconds,
      legs,
      provider: 'directions',
    };

    // Cache the result
    this.cache.set(cacheKey, result);
    this.saveToStorage(cacheKey, result);

    return result;
  }

  /**
   * Clear cache for a specific route
   */
  clearRouteCache(routeId: string | number): void {
    const cacheKey = `${this.CACHE_PREFIX}route_${routeId}`;
    this.cache.delete(cacheKey);
    localStorage.removeItem(cacheKey);
  }

  /**
   * Clear all cached routes
   */
  clearAllCache(): void {
    this.cache.clear();
    // Clear all routes cache from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Generate cache key from request
   */
  private generateCacheKey(origin: RoutePoint, destination: RoutePoint, options: RouteOptions): string {
    const originStr = `${origin.lat},${origin.lng}`;
    const destinationStr = `${destination.lat},${destination.lng}`;
    const waypointsStr = options.waypoints?.map((w) => `${w.lat},${w.lng}`).join('|') || '';
    const travelMode = options.travelMode || 'DRIVE';

    return `${this.CACHE_PREFIX}${originStr}_${destinationStr}_${waypointsStr}_${travelMode}`;
  }

  /**
   * Save to localStorage with expiry
   */
  private saveToStorage(key: string, data: RouteResult): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        expiry: Date.now() + this.CACHE_EXPIRY_MS,
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save directions routing cache to localStorage:', error);
    }
  }

  /**
   * Get from localStorage with expiry check
   */
  private getFromStorage(key: string): RouteResult | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const cacheData = JSON.parse(stored);

      // Check if cache has expired
      if (Date.now() > cacheData.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.warn('Failed to load directions routing cache from localStorage:', error);
      return null;
    }
  }

  /**
   * Load cache from localStorage on service initialization
   */
  private loadCacheFromStorage(): void {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          const cached = this.getFromStorage(key);
          if (cached) {
            this.cache.set(key, cached);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to load directions routing cache from localStorage:', error);
    }
  }
}
